import type { LlmCallMessage, PipelineVerboseStep } from "@/features/chat/lib/turnStream";
import { isReaction, type FeedbackState } from "@/shared/lib/feedbackReaction";

/** The requesting user's own feedback on a conversation turn. `reaction` is null when they
 * have none — but on the read path here, a `feedback` field is only ever present at all when
 * a persisted reaction exists, so in practice this is never constructed with `reaction: null`. */
export type ChatTurnFeedback = FeedbackState;

export type TurnResponseStatus =
  | "pending"
  | "streaming"
  | "waiting_for_clarification"
  | "complete"
  | "error"
  | "terminated";

export type ClarificationOption = {
  id: string;
  label: string;
  description?: string | null;
  option_type: string;
  value?: Record<string, unknown>;
  confidence?: number | null;
};

export type ClarificationQuestionItem = {
  id: string;
  question: string;
  reason?: string;
  missing_or_ambiguous_terms?: string[];
  options: ClarificationOption[];
  allow_other?: boolean;
};

export type ClarificationAnswerItem = {
  question_id: string;
  selected_option_id: string;
  custom_text?: string | null;
};

export type ClarificationRequest = {
  version?: number;
  clarification_type: "keyword" | "ambiguity" | "follow_up" | "validator";
  source_node: string;
  questions: ClarificationQuestionItem[];
  resume_node?: string;
  // v1 compat
  question?: string;
  reason?: string;
  missing_or_ambiguous_terms?: string[];
  options?: ClarificationOption[];
  allow_other?: boolean;
};

export function normalizeClarificationRequest(
  value: ClarificationRequest | Record<string, unknown> | null | undefined,
): ClarificationRequest | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const row = value as Record<string, unknown>;
  const rawQuestions = row.questions;
  if (Array.isArray(rawQuestions) && rawQuestions.length > 0) {
    const questions = rawQuestions.filter(
      (item): item is ClarificationQuestionItem =>
        typeof item === "object" &&
        item !== null &&
        typeof (item as ClarificationQuestionItem).id === "string" &&
        typeof (item as ClarificationQuestionItem).question === "string" &&
        Array.isArray((item as ClarificationQuestionItem).options),
    );
    if (questions.length === 0) {
      return null;
    }
    return {
      ...(value as ClarificationRequest),
      version: typeof row.version === "number" ? row.version : 2,
      clarification_type: row.clarification_type as ClarificationRequest["clarification_type"],
      source_node: String(row.source_node ?? "unknown"),
      questions,
      resume_node: typeof row.resume_node === "string" ? row.resume_node : undefined,
    };
  }

  const question = row.question;
  const options = row.options;
  if (typeof question !== "string" || !Array.isArray(options)) {
    return null;
  }

  return {
    version: typeof row.version === "number" ? row.version : 1,
    clarification_type: row.clarification_type as ClarificationRequest["clarification_type"],
    source_node: String(row.source_node ?? "unknown"),
    questions: [
      {
        id: "q1",
        question,
        reason: typeof row.reason === "string" ? row.reason : undefined,
        missing_or_ambiguous_terms: Array.isArray(row.missing_or_ambiguous_terms)
          ? (row.missing_or_ambiguous_terms as string[])
          : undefined,
        options: options as ClarificationOption[],
        allow_other: typeof row.allow_other === "boolean" ? row.allow_other : true,
      },
    ],
    resume_node: typeof row.resume_node === "string" ? row.resume_node : undefined,
    question,
    reason: typeof row.reason === "string" ? row.reason : undefined,
    options: options as ClarificationOption[],
  };
}

export type ClarificationResponseResult = {
  turn_id: string;
  response_status: "streaming";
  stream_url: string;
};

export type AnswerKind = "semantic" | "data" | "chart" | "hybrid" | "out_of_scope";

export type ChatTabularData = {
  columns: string[];
  rows: Record<string, unknown>[];
  row_count?: number;
  truncated?: boolean;
};

export type ChartType = "line" | "bar" | "area" | "pie" | "scatter" | "combo";
export type ChartAxisType = "time" | "category" | "number";
export type MeasureEncoding = "line" | "bar";

export type ChartDimension = {
  key: string;
  label?: string | null;
  type?: ChartAxisType;
};

export type ChartMeasure = {
  key: string;
  label?: string | null;
  encoding?: MeasureEncoding | null;
  predicted?: boolean | null;
  y_axis_index?: 0 | 1 | null;
};

export type GeneralizedChartDefinition = {
  id: string;
  title?: string | null;
  description?: string | null;
  dimension: ChartDimension;
  measures: ChartMeasure[];
  recommended_chart_type: ChartType;
  is_trend?: boolean;
  rationale?: string | null;
  stack_dimension?: ChartDimension | null;
};

/** @deprecated Use GeneralizedChartDefinition */
export type ChartDefinition = GeneralizedChartDefinition;

export type CardFormat = "number" | "text";

/**
 * A single-metric card, rendered instead of a chart when a result has only
 * one row (no axis to plot). One card per number/text column; date-like
 * columns never produce a card.
 */
export type CardDefinition = {
  id: string;
  title: string;
  column: string;
  value: unknown;
  format: CardFormat;
};

export type ChatChartCollection = {
  version?: number;
  data: Record<string, unknown>[];
  charts: GeneralizedChartDefinition[];
  cards?: CardDefinition[];
};

/** @deprecated Legacy single-chart shape; prefer ChatChartCollection */
export type ChatChartData = ChatChartCollection;

export type AnswerConfidenceLevel = "high" | "medium" | "low";

export type AnswerConfidence = {
  score: number;
  level: AnswerConfidenceLevel;
  signals: string[];
};

/**
 * How the answer from the domain's uploaded documents related to the warehouse answer.
 * Structured (warehouse) data is always authoritative; a conflict is surfaced, never resolved.
 */
export type DocumentAgreement =
  | "supports"
  | "complements"
  | "conflicts"
  | "document_only"
  | "not_relevant";

export type DocumentCitation = {
  sourceFile: string;
  page?: number | null;
};

/**
 * One cited file-and-page behind an answer. Richer than the bare filename in `documentSources`:
 * `previewImage` is the retrieved page image itself, as a `data:` URI, present only for a cited
 * passage that was a page image and only for the first couple of them.
 */
export type DocumentSourceRef = {
  sourceFile: string;
  page?: number | null;
  docTitle?: string | null;
  timeline?: string | null;
  isImage: boolean;
  previewImage?: string | null;
};

export type DocumentConflict = {
  subject: string;
  structuredValue: string;
  documentValue: string;
  structuredSource?: string | null;
  documentSources: DocumentCitation[];
  likelyCause?: string | null;
  /**
   * Both sides measured the same period, so the difference is an unresolved ambiguity in the data
   * rather than a document that predates the warehouse. Escalated in the UI for that reason.
   */
  samePeriod: boolean;
};

export type GeneratedQuery = {
  purpose?: string;
  sql?: string | null;
  status?: string;
  row_count?: number;
  execution_ms?: number | null;
  error_message?: string | null;
  /** True when the query combines measures across multiple physical tables (a composite metric). */
  cross_table_metric?: boolean;
  /** Distinct physical tables a cross-table metric spans. */
  source_tables?: string[];
};

export type AssistantTurnData = {
  text: string;
  summary?: string | null;
  insights?: string[] | null;
  why?: string | null;
  action?: string | null;
  businessImpact?: string | null;
  followUpQuestions: string[];
  verboseLog: PipelineVerboseStep[];
  answerKind?: AnswerKind;
  tabularData?: ChatTabularData | null;
  chartData?: ChatChartData | null;
  generatedQueries?: GeneratedQuery[] | null;
  clarificationRequest?: ClarificationRequest | null;
  answerConfidence?: AnswerConfidence | null;
  documentAgreement?: DocumentAgreement | null;
  documentConflicts?: DocumentConflict[];
  documentSources?: string[];
  documentSourceRefs?: DocumentSourceRef[];
};

export type ConversationTurn = {
  turnId: string;
  turnIndex: number;
  userQuestion: string;
  assistantData: AssistantTurnData | null;
  status: TurnResponseStatus;
  clarificationRequest?: ClarificationRequest | null;
  feedback?: ChatTurnFeedback | null;
};

export type ChatUserMessage = {
  id: string;
  role: "user";
  content: string;
};

export type FirstTurnResponse = {
  session_id: string;
  turn_id: string;
  turn_index: number;
  status: "pending";
};

export type FollowUpTurnResponse = {
  session_id: string;
  turn_id: string;
  turn_index: number;
  status: "pending";
};

export type TerminateTurnResponse = {
  turn_id: string;
  status: "terminated";
};

export type VerboseStepItem = {
  step_index: number;
  step_type: string;
  step_title: string | null;
  summary: string | null;
  detail: string;
  payload: Record<string, unknown> | null;
};

export type SessionTurnItem = {
  turn_id: string;
  turn_index: number;
  user_question: string;
  assistant_response: Record<string, unknown> | string | null;
  tabular_data?: Record<string, unknown> | null;
  chart_data?: Record<string, unknown> | null;
  response_status: string;
  clarification_request?: ClarificationRequest | null;
  clarification_response?: Record<string, unknown> | null;
  paused_at_node?: string | null;
  resume_node?: string | null;
  verbose_steps?: VerboseStepItem[];
  feedback?: Record<string, unknown> | null;
};

export type SessionTurnsResponse = {
  session_id: string;
  domain_id: number;
  title: string | null;
  created_at: string;
  turns: SessionTurnItem[];
};

function parseStringField(value: Record<string, unknown>, key: string): string | null {
  const raw = value[key];
  return typeof raw === "string" && raw.trim() ? raw.trim() : null;
}

function parseStringArray(value: Record<string, unknown>, key: string): string[] | null {
  const raw = value[key];
  if (!Array.isArray(raw)) {
    return null;
  }
  const items = raw.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
  return items.length > 0 ? items : null;
}

function parseFollowUpQuestions(value: Record<string, unknown>): string[] {
  const raw = value.follow_up_questions;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
}

function parseResponseText(value: Record<string, unknown> | string | null): string | null {
  if (value === null) {
    return null;
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value.text === "string") {
    return value.text;
  }
  if (typeof value.response_text === "string") {
    return value.response_text;
  }
  if (typeof value.content === "string") {
    return value.content;
  }
  return null;
}

export function mapVerboseStepItem(item: VerboseStepItem): PipelineVerboseStep & {
  request?: ClarificationRequest;
} {
  const payload = item.payload ?? {};
  const eventType =
    typeof payload.event_type === "string" ? payload.event_type : item.step_type;
  const step: PipelineVerboseStep & { request?: ClarificationRequest } = {
    stepIndex: item.step_index,
    eventType,
    stepTitle: item.step_title ?? item.step_type,
    summary: item.summary ?? item.step_title ?? item.step_type,
    detail: item.detail,
  };
  const request = parseClarificationRequest(
    payload.request as ClarificationRequest | Record<string, unknown> | undefined,
  );
  if (request) {
    step.request = request;
  }
  if (Array.isArray(payload.messages)) {
    step.messages = payload.messages.filter(
      (message): message is LlmCallMessage =>
        typeof message === "object" &&
        message !== null &&
        typeof (message as LlmCallMessage).role === "string" &&
        typeof (message as LlmCallMessage).content === "string",
    );
  }
  if (typeof payload.raw_response === "string") {
    step.rawResponse = payload.raw_response;
  }
  if (payload.input && typeof payload.input === "object") {
    step.input = payload.input as Record<string, unknown>;
  }
  if (payload.output && typeof payload.output === "object") {
    step.output = payload.output as Record<string, unknown>;
  }
  return step;
}

function parseAnswerKind(value: Record<string, unknown>): AnswerKind | undefined {
  const raw = value.answer_kind;
  if (typeof raw !== "string") {
    return undefined;
  }
  const normalized = raw.trim().toLowerCase();
  if (
    normalized === "semantic" ||
    normalized === "data" ||
    normalized === "chart" ||
    normalized === "hybrid" ||
    normalized === "out_of_scope"
  ) {
    return normalized;
  }
  return undefined;
}

function parseTabularDataFromRaw(raw: unknown): ChatTabularData | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  return parseTabularData({ tabular_data: raw });
}

/** Tolerates a missing/null `feedback` so turns still parse against a backend that predates
 * the feedback field. A `feedback` field is only ever present when a reaction was actually
 * recorded (see `_graft_turn_feedback` on the backend), so an invalid shape here is dropped
 * rather than trusted. */
function parseChatTurnFeedbackFromRaw(raw: unknown): ChatTurnFeedback | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const row = raw as Record<string, unknown>;
  if (!isReaction(row.reaction)) {
    return null;
  }
  return {
    reaction: row.reaction,
    comment: typeof row.comment === "string" ? row.comment : null,
  };
}

function parseChartCollectionFromRaw(raw: unknown): ChatChartCollection | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (Array.isArray(record.charts)) {
    const charts = record.charts
      .map((item) => parseGeneralizedChartDefinition(item))
      .filter((item): item is GeneralizedChartDefinition => item !== null);
    const cards = parseCardDefinitions(record.cards);
    if (charts.length === 0 && cards.length === 0) {
      return null;
    }
    return {
      version: typeof record.version === "number" ? record.version : 2,
      data: extractSharedData(record, record.charts),
      charts,
      ...(cards.length > 0 ? { cards } : {}),
    };
  }
  return parseLegacyChartData(record);
}

function parseTabularData(value: Record<string, unknown>): ChatTabularData | null {
  const raw = value.tabular_data;
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const columns = Array.isArray(record.columns)
    ? record.columns.filter((item): item is string => typeof item === "string")
    : [];
  const rows = Array.isArray(record.rows)
    ? record.rows.filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    : [];
  if (columns.length === 0 && rows.length === 0) {
    return null;
  }
  return {
    columns,
    rows,
    row_count: typeof record.row_count === "number" ? record.row_count : rows.length,
    truncated: typeof record.truncated === "boolean" ? record.truncated : undefined,
  };
}

const VALID_CHART_TYPES = new Set<ChartType>(["line", "bar", "area", "pie", "scatter", "combo"]);

function parseChartDimension(value: unknown): ChartDimension | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.key !== "string" || !record.key.trim()) {
    return null;
  }
  const axisType = record.type;
  return {
    key: record.key,
    label: typeof record.label === "string" ? record.label : record.key,
    type:
      axisType === "time" || axisType === "category" || axisType === "number"
        ? axisType
        : "category",
  };
}

function parseChartMeasures(value: unknown): ChartMeasure[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item) => {
      const encoding =
        item.encoding === "line" || item.encoding === "bar" ? item.encoding : null;
      return {
        key: typeof item.key === "string" ? item.key : "",
        label: typeof item.label === "string" ? item.label : null,
        encoding: encoding as MeasureEncoding | null,
        predicted: typeof item.predicted === "boolean" ? item.predicted : null,
        y_axis_index: item.y_axis_index === 0 || item.y_axis_index === 1 ? (item.y_axis_index as 0 | 1) : null,
      };
    })
    .filter((item) => item.key.length > 0);
}

function parseLegacySeriesAsMeasures(value: unknown, chartType: ChartType): ChartMeasure[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item) => {
      const key = typeof item.key === "string" ? item.key : "";
      let encoding: MeasureEncoding | null = null;
      if (chartType === "combo") {
        const seriesType = item.type;
        if (seriesType === "line" || seriesType === "bar") {
          encoding = seriesType;
        }
      }
      return {
        key,
        label: typeof item.label === "string" ? item.label : null,
        encoding,
      };
    })
    .filter((item) => item.key.length > 0);
}

function parseGeneralizedChartDefinition(value: unknown): GeneralizedChartDefinition | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;

  const recommendedRaw = record.recommended_chart_type ?? record.chart_type;
  if (typeof recommendedRaw !== "string" || !VALID_CHART_TYPES.has(recommendedRaw as ChartType)) {
    return null;
  }
  const recommendedChartType = recommendedRaw as ChartType;

  const dimension = parseChartDimension(record.dimension ?? record.x);
  if (!dimension) {
    return null;
  }

  let measures = parseChartMeasures(record.measures);
  if (measures.length === 0) {
    measures = parseLegacySeriesAsMeasures(record.series, recommendedChartType);
  }
  if (measures.length === 0) {
    return null;
  }

  return {
    id: typeof record.id === "string" ? record.id : "chart_1",
    title: typeof record.title === "string" ? record.title : null,
    recommended_chart_type: recommendedChartType,
    is_trend: typeof record.is_trend === "boolean" ? record.is_trend : false,
    dimension,
    measures,
    rationale: typeof record.rationale === "string" ? record.rationale : null,
    stack_dimension: parseChartDimension(record.stack_dimension),
  };
}

const VALID_CARD_FORMATS = new Set<CardFormat>(["number", "text"]);

function parseCardDefinition(value: unknown): CardDefinition | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.id !== "string" || typeof record.column !== "string") {
    return null;
  }
  const format = record.format;
  if (typeof format !== "string" || !VALID_CARD_FORMATS.has(format as CardFormat)) {
    return null;
  }
  return {
    id: record.id,
    title: typeof record.title === "string" ? record.title : record.column,
    column: record.column,
    value: record.value,
    format: format as CardFormat,
  };
}

function parseCardDefinitions(value: unknown): CardDefinition[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => parseCardDefinition(item))
    .filter((item): item is CardDefinition => item !== null);
}

function extractSharedData(
  record: Record<string, unknown>,
  chartsRaw: unknown[],
): Record<string, unknown>[] {
  if (Array.isArray(record.data)) {
    return record.data.filter(
      (item): item is Record<string, unknown> => typeof item === "object" && item !== null,
    );
  }
  if (Array.isArray(record.source_rows)) {
    return record.source_rows.filter(
      (item): item is Record<string, unknown> => typeof item === "object" && item !== null,
    );
  }
  for (const chart of chartsRaw) {
    if (!chart || typeof chart !== "object") {
      continue;
    }
    const chartRecord = chart as Record<string, unknown>;
    if (Array.isArray(chartRecord.data)) {
      return chartRecord.data.filter(
        (item): item is Record<string, unknown> => typeof item === "object" && item !== null,
      );
    }
  }
  return [];
}

function parseLegacyChartData(record: Record<string, unknown>): ChatChartCollection | null {
  const chartType = record.chart_type;
  if (typeof chartType !== "string" || chartType === "table") {
    return null;
  }
  if (!VALID_CHART_TYPES.has(chartType as ChartType)) {
    return null;
  }
  const xKey = typeof record.x === "string" ? record.x : null;
  const yKeys = Array.isArray(record.y)
    ? record.y.filter((item): item is string => typeof item === "string")
    : [];
  if (!xKey || yKeys.length === 0) {
    return null;
  }
  const normalizedType = chartType as ChartType;
  return {
    version: 2,
    data: [],
    charts: [
      {
        id: "chart_1",
        recommended_chart_type: normalizedType,
        title: typeof record.title === "string" ? record.title : null,
        is_trend: normalizedType === "line" || normalizedType === "area",
        dimension: { key: xKey, label: xKey, type: "category" },
        measures: yKeys.map((key) => ({
          key,
          label: key,
          encoding: normalizedType === "combo" ? ("line" as MeasureEncoding) : null,
        })),
      },
    ],
  };
}

function parseChartData(value: Record<string, unknown>): ChatChartCollection | null {
  const raw = value.chart_data;
  if (!raw || typeof raw !== "object") {
    return null;
  }
  return parseChartCollectionFromRaw(raw);
}

function parseGeneratedQueries(value: Record<string, unknown>): GeneratedQuery[] | null {
  const raw = value.generated_queries;
  if (Array.isArray(raw)) {
    return raw.filter((item): item is GeneratedQuery => typeof item === "object" && item !== null);
  }
  if (raw && typeof raw === "object") {
    const queries = (raw as Record<string, unknown>).queries;
    if (Array.isArray(queries)) {
      return queries.filter((item): item is GeneratedQuery => typeof item === "object" && item !== null);
    }
  }
  return null;
}

function parseClarificationRequest(
  value: ClarificationRequest | Record<string, unknown> | null | undefined,
): ClarificationRequest | null {
  return normalizeClarificationRequest(value);
}

const VALID_ANSWER_CONFIDENCE_LEVELS = new Set<AnswerConfidenceLevel>(["high", "medium", "low"]);

function parseAnswerConfidence(value: Record<string, unknown>): AnswerConfidence | null {
  const raw = value.answer_confidence;
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const level = record.level;
  if (
    typeof record.score !== "number" ||
    typeof level !== "string" ||
    !VALID_ANSWER_CONFIDENCE_LEVELS.has(level as AnswerConfidenceLevel)
  ) {
    return null;
  }
  return {
    score: record.score,
    level: level as AnswerConfidenceLevel,
    signals: Array.isArray(record.signals)
      ? record.signals.filter((item): item is string => typeof item === "string")
      : [],
  };
}

const VALID_DOCUMENT_AGREEMENTS = new Set<DocumentAgreement>([
  "supports",
  "complements",
  "conflicts",
  "document_only",
  "not_relevant",
]);

function parseDocumentAgreement(value: Record<string, unknown>): DocumentAgreement | null {
  const raw = value.document_agreement;
  if (typeof raw !== "string" || !VALID_DOCUMENT_AGREEMENTS.has(raw as DocumentAgreement)) {
    return null;
  }
  return raw as DocumentAgreement;
}

function parseDocumentCitations(value: unknown): DocumentCitation[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }
    const record = item as Record<string, unknown>;
    const sourceFile = record.source_file;
    if (typeof sourceFile !== "string" || sourceFile.length === 0) {
      return [];
    }
    return [{ sourceFile, page: typeof record.page === "number" ? record.page : null }];
  });
}

/**
 * Conflicts are only rendered when both sides carry a value — a one-sided entry is not a
 * disagreement, and showing it as one would put a phantom conflict in front of the reader.
 */
function parseDocumentConflicts(value: Record<string, unknown>): DocumentConflict[] {
  const raw = value.conflicts;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }
    const record = item as Record<string, unknown>;
    const subject = record.subject;
    const structuredValue = record.structured_value;
    const documentValue = record.document_value;
    if (
      typeof subject !== "string" ||
      typeof structuredValue !== "string" ||
      typeof documentValue !== "string"
    ) {
      return [];
    }
    return [
      {
        subject,
        structuredValue,
        documentValue,
        structuredSource:
          typeof record.structured_source === "string" ? record.structured_source : null,
        documentSources: parseDocumentCitations(record.document_sources),
        likelyCause: typeof record.likely_cause === "string" ? record.likely_cause : null,
        samePeriod: record.same_period === true,
      },
    ];
  });
}

/**
 * Cited sources with their page and preview location. A ref with no `source_file` is dropped: it
 * cannot be labelled, linked, or previewed, so there is nothing to render for it.
 */
function parseDocumentSourceRefs(value: Record<string, unknown>): DocumentSourceRef[] {
  const raw = value.document_source_refs;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }
    const record = item as Record<string, unknown>;
    const sourceFile = record.source_file;
    if (typeof sourceFile !== "string" || sourceFile.length === 0) {
      return [];
    }
    return [
      {
        sourceFile,
        page: typeof record.page === "number" ? record.page : null,
        docTitle: typeof record.doc_title === "string" ? record.doc_title : null,
        timeline: typeof record.timeline === "string" ? record.timeline : null,
        isImage: record.is_image === true,
        // Only a `data:` URI is accepted. This value goes straight into an `img src`, so anything
        // else — an `http(s)` URL, a `javascript:` scheme — is a request to load or run something
        // the pipeline did not put there.
        previewImage:
          typeof record.preview_image === "string" && record.preview_image.startsWith("data:image/")
            ? record.preview_image
            : null,
      },
    ];
  });
}

function clarificationFromVerboseSteps(
  verboseLog: PipelineVerboseStep[],
): ClarificationRequest | null {
  for (let index = verboseLog.length - 1; index >= 0; index -= 1) {
    const step = verboseLog[index];
    if (step.eventType !== "clarification_required") {
      continue;
    }
    const payload = step as PipelineVerboseStep & { request?: ClarificationRequest };
    if (payload.request) {
      return parseClarificationRequest(payload.request);
    }
  }
  return null;
}

export function mapSessionTurnItemToConversationTurn(item: SessionTurnItem): ConversationTurn {
  const text = parseResponseText(item.assistant_response);
  const responseObject =
    item.assistant_response && typeof item.assistant_response === "object"
      ? item.assistant_response
      : null;
  const followUpQuestions = responseObject ? parseFollowUpQuestions(responseObject) : [];
  const answerKind = responseObject ? parseAnswerKind(responseObject) : undefined;
  const summary = responseObject ? parseStringField(responseObject, "summary") : null;
  const insights = responseObject ? parseStringArray(responseObject, "insights") : null;
  const why = responseObject ? parseStringField(responseObject, "why") : null;
  const action = responseObject ? parseStringField(responseObject, "action") : null;
  const businessImpact = responseObject ? parseStringField(responseObject, "business_impact") : null;
  const answerConfidence = responseObject ? parseAnswerConfidence(responseObject) : null;
  const documentAgreement = responseObject ? parseDocumentAgreement(responseObject) : null;
  const documentConflicts = responseObject ? parseDocumentConflicts(responseObject) : [];
  const documentSources = responseObject ? parseStringArray(responseObject, "document_sources") : null;
  const documentSourceRefs = responseObject ? parseDocumentSourceRefs(responseObject) : [];
  const tabularData =
    parseTabularDataFromRaw(item.tabular_data) ??
    (responseObject ? parseTabularData(responseObject) : null);
  const chartData =
    parseChartCollectionFromRaw(item.chart_data) ??
    (responseObject ? parseChartData(responseObject) : null);
  const generatedQueries = responseObject ? parseGeneratedQueries(responseObject) : null;
  const verboseLog = (item.verbose_steps ?? []).map(mapVerboseStepItem);
  const status = normalizeTurnStatus(item.response_status);
  const isInProgress =
    status === "pending" || status === "streaming" || status === "waiting_for_clarification";
  const isTerminated = status === "terminated";
  const clarificationRequest =
    parseClarificationRequest(item.clarification_request) ??
    clarificationFromVerboseSteps(verboseLog);
  const feedback = parseChatTurnFeedbackFromRaw(item.feedback);

  return {
    turnId: item.turn_id,
    turnIndex: item.turn_index,
    userQuestion: item.user_question,
    clarificationRequest,
    feedback,
    assistantData:
      text !== null || verboseLog.length > 0 || isInProgress || isTerminated || clarificationRequest
        ? {
            text: text ?? "",
            summary,
            insights,
            why,
            action,
            businessImpact,
            followUpQuestions,
            verboseLog,
            answerKind,
            tabularData,
            chartData,
            generatedQueries,
            clarificationRequest,
            answerConfidence,
            // Omitted entirely rather than defaulted, so a turn that drew on no documents
            // carries exactly the shape it always has.
            ...(documentAgreement ? { documentAgreement } : {}),
            ...(documentConflicts.length > 0 ? { documentConflicts } : {}),
            ...(documentSources && documentSources.length > 0 ? { documentSources } : {}),
            ...(documentSourceRefs.length > 0 ? { documentSourceRefs } : {}),
          }
        : null,
    status,
  };
}

function normalizeTurnStatus(status: string): TurnResponseStatus {
  if (
    status === "pending" ||
    status === "streaming" ||
    status === "waiting_for_clarification" ||
    status === "complete" ||
    status === "error" ||
    status === "terminated"
  ) {
    return status;
  }
  return "complete";
}
